import React, {useEffect, useState} from 'react';
import classnames from 'classnames';
import {Button, Icon, Text} from '@albert-io/atomic';
import type {AuthoringSupplementModelV1} from 'resources/augmented/AuthoringSupplement/AuthoringSupplementModel.v1';
import type {SupplementModelV1} from 'resources/augmented/Supplement/SupplementModel.v1';
import {ANNOTATION_SHIELD_EXEMPT_PROPS} from 'client/components/PracticeView/Annotations/shared/annotationFreeze';
import {getCroppedImageUrl} from 'client/SupplementTypes/Image/utils';

type ImageSupplement = SupplementModelV1 | AuthoringSupplementModelV1;

interface ImageRendererProps extends PropsWithClassNameOptional {
  supplement: ImageSupplement;
}

interface ImageAttemptProps extends ImageRendererProps {
  attempt: number;
  onRetry: () => void;
}

/**
 * A retried `<img>` with the same `src` can be served the browser's cached failure, so each
 * retry gets a fresh URL. imgproxy drops the request query string, so cropped URLs are safe too.
 */
function withRetryParam(src: string, attempt: number): string {
  if (attempt === 0) {
    return src;
  }
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;
}

const ImageAttempt = ({supplement, className, attempt, onRetry}: ImageAttemptProps) => {
  const [hasError, setHasError] = useState(false);
  const [croppedSrc, setCroppedSrc] = useState<string | null>(null);

  const {path, href} = supplement.getImageLocation();
  const isCropped = !supplement.getCropValues().isEmpty();

  useEffect(() => {
    if (!path || !isCropped) {
      return undefined;
    }
    let cancelled = false;
    getCroppedImageUrl(supplement).then(
      (src: string) => {
        if (!cancelled) {
          setCroppedSrc(src);
        }
      },
      () => {
        if (!cancelled) {
          setHasError(true);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [supplement, path, isCropped]);

  if (!path) {
    return null;
  }

  const alt = supplement.getAlttext();

  if (hasError) {
    return (
      <div
        role='alert'
        data-testid='image-renderer__load-error'
        className={classnames(
          className,
          'gap-x2 p-x3 flex flex-col items-center justify-center text-center'
        )}
      >
        <Icon icon={['far', 'image']} color='secondary' size='standalone' />
        <Text as='p' bold>
          This image didn&apos;t load.
        </Text>
        {alt && (
          <Text as='p' color='secondary' size='s'>
            {alt}
          </Text>
        )}
        {/* Shield-exempt: a failed image must stay retryable while annotation mode freezes the question. */}
        <Button
          {...ANNOTATION_SHIELD_EXEMPT_PROPS}
          data-testid='image-renderer__retry-btn'
          size='s'
          variant='outlined'
          color='secondary'
          onClick={onRetry}
        >
          <Icon icon={['far', 'rotate-right']} className='mr-x1' />
          Retry
        </Button>
      </div>
    );
  }

  const src = isCropped ? croppedSrc : href;
  if (!src) {
    return null;
  }

  return (
    <img
      className={className}
      src={withRetryParam(src, attempt)}
      alt={alt}
      onContextMenu={(e) => e.preventDefault()}
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Renders an image supplement's `<img>`, resolving cropped images through imgproxy.
 * A load failure shows a visible fallback with the alt text and a Retry button that
 * re-requests the image in place. The attempt is keyed on the image path and retry count, so a
 * retry or a swapped image remounts it with clean state.
 */
const ImageRenderer = ({supplement, className}: ImageRendererProps) => {
  const [attempt, setAttempt] = useState(0);
  return (
    <ImageAttempt
      key={`${supplement.getImageLocation().path}:${attempt}`}
      supplement={supplement}
      className={className}
      attempt={attempt}
      onRetry={() => setAttempt((current) => current + 1)}
    />
  );
};

export default ImageRenderer;
